import React from 'react';
import PropTypes from 'prop-types';
import CommonAppSetup from '../../../common/common-app-setup/CommonAppSetup';
import ValidationType from '../../../common/validation/ValidationType';
import ValidationEngine from '../../../common/validation/ValidationEngine';
import InputContainer from '../input-container/InputContainer';
import Components from '../../../common/Components';
import joinClassNames from "../../../common/JoinClassNames";
import DirtyEngine from "../../../common/dirty/DirtyEngine";
import ChildPosition, {positionChildren} from "../ChildPosition";
import _ from 'lodash';

const propTypes = {
	// css class to add to each button
	buttonClassName: PropTypes.string,

	// children node that allows you to pass in other elements
	children: PropTypes.node,

	// put the children before the label or after the input
	childrenPosition: PropTypes.oneOf([
		ChildPosition.AFTER,
		ChildPosition.BEFORE,
	]),

	// what extra classes should go on input-container wrapper?
	className: PropTypes.string,

	// tracks dirty state of fields
	dirtyEngine: PropTypes.instanceOf(DirtyEngine),

	// if true then the input will be disabled
	disabled: PropTypes.bool,

	// An error message to show. Overrides validation errors.
	errorMessage: PropTypes.string,

	// the name of the redux field being edited and id of the input for label. for=
	field: PropTypes.string.isRequired,

	// is this input "dirty" - trumps DirtyEngine
	isDirty: PropTypes.bool,

	// the label/placeholder to show
	label: PropTypes.string.isRequired,

	// should allow multiple selections? value comes in as array and goes out as array
	multiSelect: PropTypes.bool,

	// triggers when the last button loses focus
	onBlur: PropTypes.func,

	// what function to trigger when a change is detected (could be a dispatcher);
	// parameters: field, value
	onChange: PropTypes.func,

	// the options to show as buttons to select from
	options: PropTypes.arrayOf(PropTypes.shape({value: PropTypes.any.isRequired, label: PropTypes.string.isRequired})).isRequired,

	// When the component mounts should the input be given focus.
	setInitialFocus: PropTypes.bool,

	// should the label be shown?
	showLabel: PropTypes.bool,

	// css size style names: use Size... constants
	size: PropTypes.string,

	// the validation rules and results
	validationEngine: PropTypes.instanceOf(ValidationEngine),

	// the value of this field
	value: PropTypes.oneOfType([
		// single select
		PropTypes.string,
		// multi select
		PropTypes.arrayOf(PropTypes.string),
	]).isRequired,
};

const defaultProps = {
	buttonClassName: undefined,
	children: undefined,
	childrenPosition: ChildPosition.AFTER,
	className: undefined,
	dirtyEngine: undefined,
	disabled: false,
	errorMessage: undefined,
	isDirty: undefined,
	multiSelect: false,
	onBlur: undefined,
	onChange: undefined,
	setInitialFocus: false,
	showLabel: true,
	size: '',
	validationEngine: new ValidationEngine(),
};

export default class SegmentedButton extends React.Component {
	constructor(props) {
		super(props);

		this.onButtonPress = this.onButtonPress.bind(this);
		this.onBlur = this.onBlur.bind(this);

		this.firstButtonRef = React.createRef();
		this.lastButtonRef = React.createRef();
		this.segmentedButtonWrapperRef = React.createRef();
		this.dirtyEngine = this.props.dirtyEngine ? this.props.dirtyEngine : new DirtyEngine({[this.props.field]: this.props.value});

		this.state = {
			hasBlurred: false
		};
	}

	componentDidMount() {
		if (this.props.setInitialFocus) {
			this.firstButtonRef.current.focus();
		}
	}

	onBlur(e) {
		let t = setTimeout(() => {
			if (!this.segmentedButtonWrapperRef.current.querySelector('button:focus')) {
				this.setState({hasBlurred: true});
				if (this.props.onBlur) {
					this.props.onBlur(e);
				}
			}
			clearTimeout(t);
		}, 1);
	}

	onButtonPress(newValue) {
		let useValue = newValue;
		if (this.props.multiSelect) {
			if (this.props.value.includes(useValue)) {
				useValue = this.props.value.filter(v => v !== useValue);
			} else {
				useValue = this.props.value.concat([useValue]);
			}
		}
		this.props.onChange && this.props.onChange(this.props.field, useValue);
	}

	setRef(options, index) {
		let result;
		if (index === 0) {
			result = this.firstButtonRef;
		} else if (index === options.length - 1) {
			result = this.lastButtonRef;
		}
		return result;
	}

	render() {
		const CommonAppSetupComponent = Components.SEGMENTED_BUTTON;

		const validationError = this.props.validationEngine.getFormError(this.props.field, this.state.hasBlurred, this.props.errorMessage);
		const required = this.props.validationEngine.fieldHasRule(this.props.field, ValidationType.REQUIRED);

		const valuesArray = _.castArray(this.props.value);
		return (
			<InputContainer className={joinClassNames(
				this.props.className,
				required ? 'required' : '',
				validationError ? 'error' : undefined,
				CommonAppSetup.getClassName(CommonAppSetupComponent, 'wrapper'),
				this.dirtyEngine.isDirtyClassName({[this.props.field]: this.props.value}, this.props.field, this.props.isDirty)
			)}>
				{positionChildren(this, ChildPosition.BEFORE)}
				<label htmlFor={this.props.field} className={joinClassNames(
					this.props.showLabel ? '' : 'visually-hidden',
					CommonAppSetup.getClassName(CommonAppSetupComponent, 'label')
				)}>{this.props.label}</label>

				<div
					className={joinClassNames(
						'segmented-button-wrapper',
						this.props.size,
						CommonAppSetup.getClassName(CommonAppSetupComponent, 'buttonWrapper')
					)}
					id={this.props.field}
					ref={this.segmentedButtonWrapperRef}
				>
					{this.props.options.map((option,index,list) => (
						<button
							className={joinClassNames(
								this.props.buttonClassName,
								valuesArray.includes(option.value) ? 'selected' : '',
								CommonAppSetup.getClassName(CommonAppSetupComponent, 'button')
							)}
							disabled={this.props.disabled}
							key={option.value}
							onBlur={this.onBlur}
							onClick={() => this.onButtonPress(option.value)}
							ref={this.setRef(list, index)}
							type="button"
						>{option.label}</button>
					))}
				</div>
				{positionChildren(this, ChildPosition.AFTER)}
				{validationError}
			</InputContainer>
		);
	}
}

SegmentedButton.propTypes = propTypes;
SegmentedButton.defaultProps = defaultProps;
